import { React, useEffect, useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import { CompanyEdit } from "..";
import CloseIcon from "@mui/icons-material/Close";
import ReactPaginate from "react-paginate";
import "../../styles/Overflow.css";

function CompanyTable(props) {
  const { companies, updateTable, setUpdateTable } = props;

  const [itemOffset, setItemOffset] = useState(0);
  const [memberships, setMemberships] = useState([]);
  const [statuses, setStatuses] = useState([]);

  // Fetch memberships and statuses once
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [membershipRes, statusRes] = await Promise.all([
          axios.get("/admin/getMemberships"),
          axios.get("/admin/getStatuses"),
        ]);

        if (membershipRes.data.success) {
          setMemberships(membershipRes.data.memberships || []);
        }

        if (statusRes.data.success) {
          setStatuses(statusRes.data.statuses || []);
        }
      } catch (error) {
        console.error("Error fetching memberships/statuses:", error);
      }
    };

    fetchOptions();
  }, []);

  const handleDelete = (employerId) => {
    const body = new FormData();
    body.append("employerId", employerId);
    axios.post("/company/deleteCompany", body).then((res) => {
      if (res.data.success) {
        setUpdateTable(!updateTable);
      }
    });
  };

  const items = companies.map((company, index) => (
    <div key={index}>
      <div className="CompanyRow-container flex rounded-md border-2 border-brand-tertiary drop-shadow-2xl mb-2">
        {/* Company Name Column */}
        <div className="CompanyRow-name text-xl flex justify-center font-brand-primary w-2/12 border-r-2 py-2">
          <p className="overFlowBigger">{company.name}</p>
        </div>

        {/* Company Username Column */}
        <div className="CompanyRow-username text-xl flex justify-center font-brand-primary w-2/12 border-r-2 py-2">
          <p className="overFlowBigger">{company.username}</p>
        </div>

        {/* Partnership Column */}
        <div className="CompanyRow-membership text-xl flex justify-center font-brand-primary w-2/12 border-r-2 py-2">
          <p className="overFlowBigger">{company.membership.membershipType}</p>
        </div>

        {/* Status Column */}
        <div className="CompanyRow-status text-xl flex justify-center font-brand-primary w-2/12 border-r-2 py-2">
          <p className="overFlowBigger">{company.status.status}</p>
        </div>

        {/* Primary Contact Column */}
        <div className="CompanyRow-primaryContact text-xl flex justify-center font-brand-primary w-3/12 border-r-2 py-2">
          <p className="overFlowBigger">{company.primaryContact.email}</p>
        </div>

        {/* Edit/Delete Buttons */}
        <div className="CompanyRow-editDelete text-xl flex justify-center w-1/12 py-2">
          <CompanyEdit
            company={company}
            updateTable={updateTable}
            setUpdateTable={setUpdateTable}
            memberships={memberships}
            statuses={statuses}
          />
          {/* <button
            className="CompanyRow-deleteButton flex"
            onClick={() => handleDelete(company.employerId)}>
            <CloseIcon fontSize="large" className="fill-brand-primary" />
          </button> */}
        </div>
      </div>
    </div>
  ));

  const itemsPerPage = 5;
  const endOffset = itemOffset + itemsPerPage;
  const currentItems = items.slice(itemOffset, endOffset);
  const pageCount = Math.ceil(items.length / itemsPerPage);

  const handlePageClick = (event) => {
    const newOffset = (event.selected * itemsPerPage) % items.length;
    setItemOffset(newOffset);
  };

  if (companies.length === 0) {
    return (
      <div className="CompanyTable bg-white p-2 rounded-xl w-full">
        No companies found.
      </div>
    );
  }

  return (
    <div className="CompanyTable bg-white p-2 rounded-xl w-full">
      <ReactPaginate
        breakLabel="..."
        nextLabel=">"
        onPageChange={handlePageClick}
        pageRangeDisplayed={5}
        pageCount={pageCount}
        previousLabel="<"
        renderOnZeroPageCount={null}
        className="flex w-full justify-between mb-6 mt-4 [&>li>a]:py-4 [&>li>a]:px-6 [&>li>a]:border [&>li>a]:rounded-lg [&>li.selected>a]:border-brand-primary"
      />
      <div className="CompanyTable-container flex rounded-md border-2 border-black drop-shadow-2xl mb-1">
        {/* Table Headers */}
        <div className="CompanyTable-name text-xl flex justify-center font-brand-primary w-2/12 border-black border-r-2 py-2">
          Name
        </div>
        <div className="CompanyTable-username text-xl flex justify-center font-brand-primary w-2/12 border-black border-r-2 py-2">
          Username
        </div>
        <div className="CompanyTable-membership text-xl flex justify-center font-brand-primary w-2/12 border-black border-r-2 py-2">
          Partnership
        </div>
        <div className="CompanyTable-status text-xl flex justify-center font-brand-primary w-2/12 border-black border-r-2 py-2">
          Status
        </div>
        <div className="CompanyTable-primaryContact text-xl flex justify-center font-brand-primary w-3/12 border-black border-r-2 py-2">
          Primary Contact
        </div>
        <div className="CompanyTable-editDelete text-xl flex justify-center w-1/12 py-2">
          Edit
        </div>
      </div>
      {currentItems}
    </div>
  );
}

CompanyTable.propTypes = {
  companies: PropTypes.arrayOf(
    PropTypes.shape({
      employerId: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      billingDate: PropTypes.string,
      paymentDate: PropTypes.string,
      status: PropTypes.shape({
        statusId: PropTypes.number,
        status: PropTypes.string,
        active: PropTypes.bool,
      }),
      membership: PropTypes.shape({
        membershipId: PropTypes.number,
        membershipType: PropTypes.string,
        cost: PropTypes.number,
      }),
      primaryContact: PropTypes.shape({
        name: PropTypes.string,
        email: PropTypes.string,
      }),
      username: PropTypes.string,
    })
  ),
  updateTable: PropTypes.bool,
  setUpdateTable: PropTypes.func,
};

CompanyTable.defaultProps = {
  companies: [],
  updateTable: false,
  setUpdateTable: () => {},
};

export default CompanyTable;
